import React, { useRef, useState } from 'react';
import BaseTableNew from 'components/atoms/base-table/new';
import BaseModal from 'components/atoms/base-modal';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import {
  useCreateCohortsInactiveDays,
  useDeleteCohortsInactiveDays,
  useGetCohortsInactiveDays,
  useUpdateCohortsInactiveDays,
} from 'infrastructure/hooks/adherence-settings';
import useAlert from 'infrastructure/hooks/utils/use-alert';

import { columns } from './columns';
import s from './styles.module.scss';
import AdherenceSettingsCohortsInactiveDaysForm from './form';

import type { IAdherenceSettingsCohortsInactiveDaysRef } from './form';
import type { IAdherenceSettingCohortsInactiveDays } from 'infrastructure/interfaces';

const AdherenceSettingsCohortsInactiveDaysTable: React.FC = () => {
  const { t } = useTranslation();

  const { Alert, showAlert } = useAlert();

  const { data, isLoading } = useGetCohortsInactiveDays();
  const { mutateAsync: create, isLoading: createLoading } =
    useCreateCohortsInactiveDays();
  const { mutateAsync: update, isLoading: updateLoading } =
    useUpdateCohortsInactiveDays();
  const { mutateAsync: remove, isLoading: removeLoading } =
    useDeleteCohortsInactiveDays();

  const loading = isLoading || createLoading || updateLoading || removeLoading;

  const formRef = useRef<IAdherenceSettingsCohortsInactiveDaysRef>(null);

  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState<
    IAdherenceSettingCohortsInactiveDays | undefined
  >(undefined);

  const title = selected ? t('controls.edit') : t('controls.create');

  const onCreate = () => {
    setOpened(true);
  };

  const onEdit = (rec: IAdherenceSettingCohortsInactiveDays) => {
    setSelected(rec);
    setOpened(true);
  };

  const onDelete = async (rec: IAdherenceSettingCohortsInactiveDays) => {
    const res = await showAlert({
      title: '',
      messageTitle: t('messages.confirmAction'),
      successBtnLabel: t('labels.delete'),
    });
    if (res) {
      await remove(rec);
    }
  };

  const onCancel = () => {
    setSelected(undefined);
    setOpened(false);
  };

  const handleSave = async (val: IAdherenceSettingCohortsInactiveDays) => {
    if (!val.guid) {
      await create(val);
    } else {
      await update(val);
    }
    setOpened(false);
  };

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  return (
    <>
      <div className={s.header}>
        <BaseButton label={t('controls.create')} onClick={onCreate} />
      </div>
      <BaseTableNew
        dataCy="adherence-settings-inactive-days-table"
        rowKey="guid"
        columns={columns({ onEdit, onDelete })}
        items={data}
        loading={loading}
        pagination={{
          current: 1,
          pageSize: 20,
          total: data.length,
        }}
        bordered
      />
      <BaseModal
        open={opened}
        title={title}
        onCancel={onCancel}
        loading={updateLoading}
        width="600px"
        smallBodyPaddingWithFooter
        withFooter={[
          <BaseButton
            dataCy="form-save-button"
            label={t('controls.save')}
            onClick={handleSubmit}
            loading={updateLoading}
            disabled={loading}
          />,
          <BaseButton
            type="secondary"
            dataCy="form-cancel-button"
            label={t('controls.cancel')}
            onClick={onCancel}
            loading={updateLoading}
            disabled={loading}
          />,
        ]}
      >
        <AdherenceSettingsCohortsInactiveDaysForm
          ref={formRef}
          loading={loading}
          data={selected}
          onSave={handleSave}
        />
      </BaseModal>
      {Alert}
    </>
  );
};

export default AdherenceSettingsCohortsInactiveDaysTable;
